import { ArrowLeft, Award, CalendarDays, ChevronLeft, ChevronRight, Clock3, RefreshCw, Shield, Trophy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getDivisionLabel } from '../online/league';
import {
  getOnlinePublicProfile,
  listOnlineMatchHistory,
  listOnlineProfileSeasonHistory,
  type OnlineLeaderboardPage,
  type OnlineMatchHistoryItem,
  type OnlinePublicProfile,
} from '../services/onlineLeague';

function dateLabel(value: string | null) {
  if (!value) return 'Sin actividad';
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value));
}

export function OnlinePublicProfilePanel({ publicPlayerId, isOwnProfile, onBack }: {
  publicPlayerId: string;
  isOwnProfile: boolean;
  onBack(): void;
}) {
  const [profile, setProfile] = useState<OnlinePublicProfile | null>(null);
  const [seasons, setSeasons] = useState<OnlineLeaderboardPage | null>(null);
  const [matches, setMatches] = useState<OnlineMatchHistoryItem[]>([]);
  const [seasonPage, setSeasonPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setSeasonPage(1); }, [publicPlayerId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all([
      getOnlinePublicProfile(publicPlayerId),
      listOnlineProfileSeasonHistory(publicPlayerId, seasonPage),
      listOnlineMatchHistory(publicPlayerId, 8),
    ]).then(([nextProfile, nextSeasons, nextMatches]) => {
      if (cancelled) return;
      setProfile(nextProfile);
      setSeasons(nextSeasons);
      setMatches(nextMatches);
    }).catch((reason) => {
      if (!cancelled) setError(reason instanceof Error ? reason.message : 'No se ha podido abrir el perfil online.');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [publicPlayerId, seasonPage]);

  if (loading) return <div className="online-profile-loading card"><RefreshCw className="spin" size={21} /> Cargando perfil público…</div>;
  if (error || !profile) return <div className="online-message online-message--error" role="alert"><span>{error ?? 'El perfil solicitado no existe.'}</span><button onClick={onBack}>Volver</button></div>;

  return <section className="online-public-profile">
    <button className="online-profile-back ghost-action" onClick={onBack}><ArrowLeft size={15} /> Volver a clasificaciones</button>
    <header className="online-profile-hero card">
      <span className="online-profile-avatar">{profile.alias.slice(0, 2).toUpperCase()}</span>
      <div><span className="card-label"><Shield size={14} /> {isOwnProfile ? 'TU PERFIL PÚBLICO' : 'PERFIL PÚBLICO'}</span><h2>{profile.alias}</h2><p>Entrenador online desde {dateLabel(profile.memberSince)} · ID público {profile.publicPlayerId.slice(0, 8)}</p></div>
      <span className={`division-pill division-${profile.bestDivision.toLowerCase()}`}><Trophy size={14} /> Máxima: {getDivisionLabel(profile.bestDivision)}</span>
    </header>

    <div className="online-profile-stats">
      <article className="card"><span>PARTIDOS</span><strong>{profile.matchesPlayed}</strong><small>{profile.seasonsPlayed} temporadas</small></article>
      <article className="card"><span>VICTORIAS</span><strong>{profile.wins}</strong><small>{profile.winRate.toFixed(1)}% de victorias</small></article>
      <article className="card"><span>EMPATES</span><strong>{profile.draws}</strong><small>{profile.losses} derrotas</small></article>
      <article className="card"><span>ÚLTIMA ACTIVIDAD</span><strong>{profile.lastMatchAt ? dateLabel(profile.lastMatchAt) : '—'}</strong><small><Clock3 size={12} /> actualizado {dateLabel(profile.updatedAt)}</small></article>
    </div>

    <div className="online-profile-columns">
      <section className="online-profile-seasons card"><header><span className="card-label"><CalendarDays size={14} /> HISTORIAL DE TEMPORADAS</span><small>{seasons?.total ?? 0} registradas</small></header>{seasons?.items.map((season) => <article key={season.seasonId}><div><b>{season.seasonName}</b><small>{season.seasonStatus === 'ACTIVE' ? 'En curso' : 'Finalizada'} · {season.matchesPlayed} partidos</small></div><span className={`division-pill division-${season.division.toLowerCase()}`}>{getDivisionLabel(season.division)}</span><strong>#{season.globalPosition}</strong><small>{season.divisionPoints} pts · {season.wins}/{season.draws}/{season.losses}</small></article>)}{!seasons?.items.length && <p className="online-ranking-empty">Aún no hay temporadas registradas.</p>}{(seasons?.totalPages ?? 1) > 1 && <footer><small>Página {seasons?.page} de {seasons?.totalPages}</small><div><button disabled={seasonPage <= 1} onClick={() => setSeasonPage((value) => Math.max(1, value - 1))}><ChevronLeft size={13} /></button><button disabled={seasonPage >= (seasons?.totalPages ?? 1)} onClick={() => setSeasonPage((value) => value + 1)}><ChevronRight size={13} /></button></div></footer>}</section>
      <section className="online-profile-matches card"><header><span className="card-label"><Trophy size={14} /> RESULTADOS RECIENTES</span><small>Datos verificados</small></header>{matches.map((match) => {
        const home = match.homePublicPlayerId === publicPlayerId;
        const ownGoals = home ? match.homeGoals : match.awayGoals;
        const rivalGoals = home ? match.awayGoals : match.homeGoals;
        const rival = home ? match.awayAlias : match.homeAlias;
        const outcome = ownGoals > rivalGoals ? 'V' : ownGoals < rivalGoals ? 'D' : 'E';
        return <article key={match.id}><span className={`online-result outcome-${outcome.toLowerCase()}`}>{outcome}</span><div><b>{ownGoals} — {rivalGoals} vs. {rival}</b><small>{dateLabel(match.settledAt)}</small></div></article>;
      })}{!matches.length && <p className="online-ranking-empty">Todavía no hay resultados liquidados.</p>}</section>
    </div>

    <div className="online-profile-awards card"><Award size={20} /><div><b>Títulos y medallas</b><p>Se mostrarán aquí cuando el cierre de temporada y los torneos concedan recompensas autoritativas. No se generan insignias ficticias.</p></div></div>
  </section>;
}
